import { preload } from 'react-dom';

import type {
  ImageOptions,
  ImageSource,
} from '@/app/components/media/image/types';
import {
  getImgSrcset,
  getMediaCondition,
  getSourceSrcset,
  isSvg,
} from '@/app/components/media/image/utils';

interface PreloadImageProps {
  /** The initial src of image to display. If you have `sources`, please set the mobile image src */
  src: string;
  /** This is for advanced responsive image render option. It can has multiple image src with different media query to select the screen size to display. This need to be ordered manually. The browser native function will looking for the matching `<source>` from top to bottom, so you need to order it wisely  */
  sources?: ImageSource[];
  /** The native HTMLImageElement property sizes allows you to specify the layout width of the image for each of a list of media conditions. <a href="https://developer.mozilla.org/en-US/docs/Web/API/HTMLImageElement/sizes" target="_blank">https://developer.mozilla.org/en-US/docs/Web/API/HTMLImageElement/sizes</a>*/
  sizes?: string;
  /** Pass image options from Contentstack Image Delivery API to adjust the image  */
  srcSetOptions?: ImageOptions;
}

const getPreloadType = (src: string) =>
  isSvg(src) ? 'image/svg+xml' : undefined;

const preloadSource = (source: ImageSource, noMedia?: boolean) => {
  const srcSet = getSourceSrcset(source);
  const mediaCondition = noMedia ? undefined : getMediaCondition(source);
  /**
   * / Note: Send option `as any` for now. Waiting for update in https://github.com/DefinitelyTyped/DefinitelyTyped/blob/master/types/react-dom/index.d.ts#L76
   * to have `media` for React 19 update
   */
  const preloadOptions = {
    as: 'image',
    fetchPriority: 'high',
    media: mediaCondition,
    imageSrcSet: srcSet,
    type: getPreloadType(source.srcset),
  } as any;
  preload(source.srcset, preloadOptions);
};

export const PreloadImage = ({
  src,
  srcSetOptions,
  sizes,
  sources = [],
}: PreloadImageProps) => {
  if (!src || typeof preload !== 'function') return null;
  /**
   * Note: Case sizes
   */
  if (sources.length === 0) {
    const isSvgImage = isSvg(src);
    preload(src, {
      as: 'image',
      fetchPriority: 'high',
      imageSrcSet: isSvgImage ? undefined : getImgSrcset(src, srcSetOptions),
      imageSizes: isSvgImage ? undefined : sizes || undefined,
      type: getPreloadType(src),
    });
    return null;
  }

  const hasSvg = sources.some((source) => isSvg(source.srcset));
  /**
   * Note: Check case only single svg src
   */
  if (hasSvg) {
    const uniqueSrcsets = [...new Set(sources.map((source) => source.srcset))];
    if (uniqueSrcsets.length === 1) {
      const source = sources[0];
      preloadSource(source, true);
      return null;
    }
  }

  let index = 0;
  for (const source of sources) {
    const isSvgImage = isSvg(source.srcset);
    // Note: Handle case svg + jpg/png
    if (isSvgImage) {
      if (source.media?.device === 'desktop') {
        const nextItem = sources[index + 1];
        source.srcset !== nextItem?.srcset && preloadSource(source);
        index++;
        continue;
      }
      if (source.media?.device === 'mobile') {
        const prevItem = sources[index - 1];
        source.srcset !== prevItem?.srcset && preloadSource(source);
        index++;
        break;
      }
    } else {
      preloadSource(source);
    }
    index++;
  }
  return null;
};
